import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import { ProcessingManager } from './processing-manager'
import { UIManager } from './ui-manager'
import { Toast } from '../components/toast'
import { llmUsageManager } from './llm-usage-manager'
import { UIHelpers } from './ui-helpers'

export class OCRRenderer {
  private apiService: ApiService
  private appState: AppState
  private processingManager: ProcessingManager
  private toast: Toast
  private uiHelpers: UIHelpers

  constructor(
    apiService: ApiService,
    appState: AppState,
    processingManager: ProcessingManager,
    _uiManager: UIManager,
    toast: Toast
  ) {
    this.apiService = apiService
    this.appState = appState
    this.processingManager = processingManager
    this.toast = toast
    this.uiHelpers = new UIHelpers(apiService, appState, _uiManager)
  }

  public initializeOCRProcessing(selections: any[]): void {
    this.renderOCRProcessingCards(selections)
    setTimeout(() => this.syncCardHeights(), 100)
    // Auto-OCR missing selections
    selections.forEach(sel => {
      if (!sel.extracted_text && sel.thumbnail_data && sel.thumbnail_data !== 'loading' && sel.thumbnail_data !== 'error') {
        this.processingManager.processIndividualOCR(sel, false)
      }
    })
  }

  private calculateContentHeight(text: string, fontSize: number, lineHeight: number = 1.4): number {
    if (!text) return 120 // minimum height when empty
    const lines = text.split('\n').length
    const lineHeightPx = fontSize * lineHeight
    const contentHeight = lines * lineHeightPx + 24 // 24px for padding
    return Math.max(contentHeight, 120) // minimum 120px
  }

  public renderOCRProcessingCards(selections: any[]): void {
    const container = document.getElementById('ocr-processing-container')
    if (!container) return

    if (selections.length === 0) {
      container.innerHTML = `
        <div class="card">
          <div class="card-body text-center py-12 text-gray-500">
            <div class="text-4xl mb-3">✂️</div>
            <h3 class="text-lg font-medium mb-2">No Snippets Available</h3>
            <p class="text-sm">Go back to Selection Mgmt to select text areas</p>
          </div>
        </div>
      `
      return
    }

    container.innerHTML = selections.map((selection, index) => {
      const storedTab = localStorage.getItem(`tab_${selection.id}`) as 'raw' | 'edited' | null
      const activeTab = storedTab ? storedTab : (selection.edited_text ? 'edited' : 'raw')
      const rawHidden = activeTab !== 'raw' ? 'hidden' : ''
      const editedHidden = activeTab !== 'edited' ? 'hidden' : ''

      // Retrieve persisted wrap preference per tab
      const savedWrapRaw = localStorage.getItem(`wrap_${selection.id}_raw`)
      const savedWrapEdited = localStorage.getItem(`wrap_${selection.id}_edited`)
      const wrapEnabledRaw = savedWrapRaw ? JSON.parse(savedWrapRaw) : !!selection.text_wrap
      const wrapEnabledEdited = savedWrapEdited ? JSON.parse(savedWrapEdited) : !!selection.text_wrap
      const wrapEnabled = activeTab === 'edited' ? wrapEnabledEdited : wrapEnabledRaw

      // Retrieve persisted confidence highlighting preference
      const savedConfidenceHighlight = localStorage.getItem(`confidence_highlight_${selection.id}`)
      const hasWordData = selection.word_confidence_data && (
        (typeof selection.word_confidence_data === 'string' && selection.word_confidence_data.length > 0) ||
        (Array.isArray(selection.word_confidence_data) && selection.word_confidence_data.length > 0)
      )
      const confidenceHighlightEnabled = savedConfidenceHighlight ? JSON.parse(savedConfidenceHighlight) : false
      
      // Retrieve persisted number highlighting preference
      const savedNumberHighlight = localStorage.getItem(`number_highlight_${selection.id}`)
      const numberHighlightEnabled = savedNumberHighlight ? JSON.parse(savedNumberHighlight) : false

      // Use class method for content height calculation

      // Check for manually saved sizes first
      const savedRawSize = localStorage.getItem(`manual_size_raw_${selection.id}`)
      const savedEditedSize = localStorage.getItem(`manual_size_edited_${selection.id}`)
      
      const rawTextHeight = savedRawSize ? 
        JSON.parse(savedRawSize).height : 
        this.calculateContentHeight(selection.extracted_text || '', selection.font_size ?? 14)
      const editedTextHeight = savedEditedSize ? 
        JSON.parse(savedEditedSize).height : 
        this.calculateContentHeight(selection.edited_text || selection.extracted_text || '', selection.font_size ?? 14)

      const rawTextWidth = savedRawSize ? JSON.parse(savedRawSize).width : '100%'
      const editedTextWidth = savedEditedSize ? JSON.parse(savedEditedSize).width : '100%'

      const textFieldStyleRaw = `
        width: ${rawTextWidth};
        height: ${rawTextHeight}px;
        min-height: 120px;
        font-family: Arial, sans-serif;
        font-size: ${selection.font_size ?? 14}px;
        line-height: 1.4;
        white-space: ${wrapEnabledRaw ? 'pre-wrap' : 'pre'};
        overflow-x: auto;
        overflow-y: auto;
        padding: 12px;
        resize: both;
        box-sizing: border-box;
      `
      
      const textFieldStyleEdited = `
        width: ${editedTextWidth};
        height: ${editedTextHeight}px;
        min-height: 120px;
        font-family: Arial, sans-serif;
        font-size: ${selection.font_size ?? 14}px;
        line-height: 1.4;
        white-space: ${wrapEnabledEdited ? 'pre-wrap' : 'pre'};
        overflow-x: auto;
        overflow-y: auto;
        padding: 12px;
        resize: both;
        box-sizing: border-box;
      `

      return `
        <div class="card ocr-processing-card shadow-sm" data-selection-id="${selection.id}">
          <div class="card-body p-6">
            <!-- Header Row -->
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-4">
                <h4 class="text-sm font-medium text-gray-900">Snippet ${index + 1}</h4>
                <h4 class="text-sm font-medium text-gray-900">Text Processing</h4>
                <div class="flex items-center gap-1.5">
                  ${selection.ocr_confidence ? `
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      selection.ocr_confidence > 80 ? 'bg-green-50 text-green-700 border border-green-200' :
                      selection.ocr_confidence > 60 ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                      'bg-red-50 text-red-700 border border-red-200'
                    }">
                      ${Math.round(selection.ocr_confidence)}% confidence
                    </span>
                  ` : ''}
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    selection.extracted_text ? 'bg-green-50 text-green-700 border border-green-200' : 
                    selection.processed === 'processing' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                    'bg-gray-50 text-gray-600 border border-gray-200'
                  }">
                    ${selection.extracted_text ? 'OCR Done' : 
                      selection.processed === 'processing' ? 'Processing' : 'Pending'}
                  </span>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <button
                  class="clean-format-btn btn btn-sm btn-secondary"
                  data-selection-id="${selection.id}"
                  ${!selection.extracted_text ? 'disabled' : ''}
                >
                  🧹 Clean-Up Formatting
                </button>
              </div>
            </div>

            <!-- Main Content -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <!-- Left: Snippet Preview -->
              <div class="snippet-preview w-full">
                <div class="snippet-thumbnail w-full">
                  ${selection.thumbnail_data && selection.thumbnail_data !== 'loading' && selection.thumbnail_data !== 'error' ? `
                    <img 
                      src="${selection.thumbnail_data}" 
                      alt="Snippet ${index + 1}" 
                      class="w-full h-auto border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:border-blue-400 transition-colors"
                      onclick="this.nextElementSibling.classList.toggle('hidden')"
                    />
                    <div class="hidden mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                      <strong>Page:</strong> ${selection.page_number} | 
                      <strong>Size:</strong> ${Math.round(selection.width)}×${Math.round(selection.height)}px |
                      <strong>OCR Status:</strong> ${selection.extracted_text ? 'Processed' : 'Pending'}
                    </div>
                  ` : `
                    <div class="w-full h-48 bg-gray-50 border border-gray-200 rounded-lg shadow-sm flex items-center justify-center text-gray-400">
                      <div class="text-center">
                        <div class="text-2xl mb-1">📄</div>
                        <div class="text-xs">Snippet ${index + 1}</div>
                        <div class="text-xs text-gray-400">Page ${selection.page_number}</div>
                      </div>
                    </div>
                  `}
                </div>
              </div>

              <!-- Right: Text Processing -->
              <div class="ocr-text-processing w-full">
                <div class="border border-gray-200 rounded-lg bg-white w-full">
                  <!-- Tab Content -->
                  <div class="p-3 w-full">
                    <div 
                      id="ocr-text-${selection.id}" 
                      class="ocr-tab-content w-full border border-gray-200 rounded-md text-xs bg-gray-50 cursor-pointer ${rawHidden}"
                      style="${textFieldStyleRaw}"
                    >${(confidenceHighlightEnabled && hasWordData) || numberHighlightEnabled ? 
                        this.renderConfidenceHighlightedText(selection.extracted_text || "", selection.word_confidence_data, 75, numberHighlightEnabled, confidenceHighlightEnabled) : 
                        (selection.extracted_text || "")}</div>

                    <div 
                      id="edited-text-${selection.id}"
                      class="ocr-tab-content w-full border border-gray-200 rounded-md text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${editedHidden}"
                      style="${textFieldStyleEdited}"
                      contenteditable="true"
                      data-placeholder="Make final edits here..."
                      ${selection.extracted_text ? '' : 'contenteditable="false"'}
                    >${(confidenceHighlightEnabled && hasWordData) || numberHighlightEnabled ? 
                        this.renderConfidenceHighlightedText(selection.edited_text || selection.extracted_text || "", selection.word_confidence_data, 75, numberHighlightEnabled, confidenceHighlightEnabled) : 
                        (selection.edited_text || selection.extracted_text || "")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="card-footer bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div class="flex flex-wrap items-center justify-between gap-y-2 gap-x-6">
              <!-- Left side: Font Size Controls + Wrap -->
              <div class="flex items-center gap-3">
                <span class="text-sm text-gray-600">Font Size:</span>
                <div class="flex items-center">
                  <button 
                    class="decrease-font-size btn btn-xs btn-outline" 
                    data-selection-id="${selection.id}"
                    title="Decrease font size"
                  >−</button>
                  <span class="px-2 text-sm">${selection.font_size ?? 14}px</span>
                  <button 
                    class="increase-font-size btn btn-xs btn-outline" 
                    data-selection-id="${selection.id}"
                    title="Increase font size"
                  >+</button>
                </div>
                <div class="flex items-center gap-2 ml-3">
                  <label class="switch mono">
                    <input type="checkbox" class="text-wrap-toggle" data-selection-id="${selection.id}" ${wrapEnabled ? 'checked' : ''}/>
                    <span class="switch-slider"></span>
                  </label>
                  <span class="text-sm text-gray-600">Wrap</span>
                </div>
              </div>

              <!-- Center: Highlight Controls -->
              <div class="flex items-center gap-4">
                <div class="flex items-center gap-2">
                  <label class="switch mono">
                    <input type="checkbox" class="confidence-highlight-toggle" data-selection-id="${selection.id}" ${confidenceHighlightEnabled ? 'checked' : ''}/>
                    <span class="switch-slider"></span>
                  </label>
                  <span class="text-sm text-gray-600">Highlight Low Confidence</span>
                </div>
                <div class="flex items-center gap-2">
                  <label class="switch mono">
                    <input type="checkbox" class="number-highlight-toggle" data-selection-id="${selection.id}" ${numberHighlightEnabled ? 'checked' : ''}/>
                    <span class="switch-slider"></span>
                  </label>
                  <span class="text-sm text-gray-600">Highlight Numbers</span>
                </div>
              </div>

              <!-- Right side: Original ↔ Final toggle -->
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-600">Original</span>
                <label class="switch subtle">
                  <input type="checkbox" class="ocr-tab-toggle" data-selection-id="${selection.id}" ${activeTab === 'edited' ? 'checked' : ''}/>
                  <span class="switch-slider"></span>
                </label>
                <span class="text-xs text-gray-600">Final</span>
              </div>
            </div>
          </div>
        </div>
      `
    }).join('')

    this.attachOCRProcessingEventListeners(selections)
  }

  private attachOCRProcessingEventListeners(selections: any[]): void {
    selections.forEach(selection => {
      const selectionId = selection.id
      const card = document.querySelector(`[data-selection-id="${selectionId}"]`) as HTMLElement
      if (!card) return


      // Clean format button
      const cleanFormatBtn = card.querySelector('.clean-format-btn') as HTMLButtonElement
      if (cleanFormatBtn) {
        cleanFormatBtn.addEventListener('click', () => {
          this.processIndividualFormatting(selection)
        })
      }

      // Tab toggle functionality
      const tabToggle = card.querySelector(`.ocr-tab-toggle[data-selection-id="${selectionId}"]`) as HTMLInputElement
      if (tabToggle) {
        tabToggle.addEventListener('change', () => {
          this.toggleOCRTab(selectionId, tabToggle.checked)
        })
      }

      // Text editing functionality
      const rawTextDiv = card.querySelector(`#ocr-text-${selectionId}`) as HTMLElement
      const editedTextDiv = card.querySelector(`#edited-text-${selectionId}`) as HTMLElement

      if (rawTextDiv) {
        rawTextDiv.addEventListener('click', () => {
          this.switchToEditedTab(selectionId)
        })
      }

      if (editedTextDiv) {
        editedTextDiv.addEventListener('input', () => {
          this.handleTextEdit(selectionId, editedTextDiv.textContent || '')
        })

        editedTextDiv.addEventListener('blur', () => {
          this.saveTextEdit(selectionId, editedTextDiv.textContent || '')
        })

        // Handle placeholder for contenteditable
        if (editedTextDiv.dataset.placeholder) {
          const placeholder = editedTextDiv.dataset.placeholder
          const updatePlaceholder = () => {
            if (editedTextDiv.textContent === '') {
              editedTextDiv.innerHTML = `<span style="color: #9ca3af;">${placeholder}</span>`
            }
          }
          
          editedTextDiv.addEventListener('focus', () => {
            if (editedTextDiv.innerHTML.includes(placeholder)) {
              editedTextDiv.innerHTML = ''
            }
          })
          
          editedTextDiv.addEventListener('blur', updatePlaceholder)
          updatePlaceholder()
        }
      }

      // Font size controls
      const decreaseBtn = card.querySelector('.decrease-font-size') as HTMLButtonElement
      const increaseBtn = card.querySelector('.increase-font-size') as HTMLButtonElement
      
      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          this.adjustFontSize(selectionId, -1)
        })
      }
      
      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => {
          this.adjustFontSize(selectionId, 1)
        })
      }

      // Text wrap toggle
      const wrapToggle = card.querySelector('.text-wrap-toggle') as HTMLInputElement
      if (wrapToggle) {
        wrapToggle.addEventListener('change', () => {
          this.toggleTextWrap(selectionId, wrapToggle.checked)
        })
      }

      // Confidence highlighting toggle
      const confidenceToggle = card.querySelector('.confidence-highlight-toggle') as HTMLInputElement
      if (confidenceToggle) {
        confidenceToggle.addEventListener('change', () => {
          this.toggleConfidenceHighlighting(selectionId, confidenceToggle.checked)
        })
      }

      // Number highlighting toggle
      const numberToggle = card.querySelector('.number-highlight-toggle') as HTMLInputElement
      if (numberToggle) {
        numberToggle.addEventListener('change', () => {
          this.toggleNumberHighlighting(selectionId, numberToggle.checked)
        })
      }
    })
  }

  private toggleOCRTab(selectionId: string, showEdited: boolean): void {
    const rawTab = document.querySelector(`#ocr-text-${selectionId}`) as HTMLElement
    const editedTab = document.querySelector(`#edited-text-${selectionId}`) as HTMLTextAreaElement
    
    if (rawTab && editedTab) {
      if (showEdited) {
        rawTab.classList.add('hidden')
        editedTab.classList.remove('hidden')
        editedTab.focus()
      } else {
        rawTab.classList.remove('hidden')
        editedTab.classList.add('hidden')
      }
      
      // Save tab preference
      localStorage.setItem(`tab_${selectionId}`, showEdited ? 'edited' : 'raw')
    }
  }

  private switchToEditedTab(selectionId: string): void {
    const tabToggle = document.querySelector(`.ocr-tab-toggle[data-selection-id="${selectionId}"]`) as HTMLInputElement
    if (tabToggle) {
      tabToggle.checked = true
      this.toggleOCRTab(selectionId, true)
    }
  }

  private handleTextEdit(selectionId: string, text: string): void {
    // Update the selection in app state
    const selections = this.appState.getSelections()
    const updatedSelections = selections.map(sel => 
      sel.id === selectionId ? { ...sel, edited_text: text } : sel
    )
    this.appState.setSelections(updatedSelections)
  }

  private async saveTextEdit(selectionId: string, text: string): Promise<void> {
    try {
      await this.apiService.updateSelection(selectionId, { edited_text: text })
      console.log('Text edit saved for selection:', selectionId)
    } catch (error) {
      console.error('Failed to save text edit:', error)
      this.toast.error('Failed to save text edit')
    }
  }

  private adjustFontSize(selectionId: string, delta: number): void {
    const selections = this.appState.getSelections()
    const selection = selections.find(s => s.id === selectionId)
    if (!selection) return

    const currentSize = selection.font_size ?? 14
    const newSize = Math.max(8, Math.min(24, currentSize + delta))
    
    // Update selection
    const updatedSelections = selections.map(sel => 
      sel.id === selectionId ? { ...sel, font_size: newSize } : sel
    )
    this.appState.setSelections(updatedSelections)
    
    // Re-render the card to apply new font size
    this.renderOCRProcessingCards(updatedSelections)
  }

  private toggleTextWrap(selectionId: string, enabled: boolean): void {
    const activeTab = localStorage.getItem(`tab_${selectionId}`) as 'raw' | 'edited' | null
    const tabKey = activeTab === 'edited' ? 'edited' : 'raw'
    
    // Save wrap preference
    localStorage.setItem(`wrap_${selectionId}_${tabKey}`, JSON.stringify(enabled))
    
    // Update the text field style
    const textField = document.querySelector(`#${activeTab === 'edited' ? 'edited-text' : 'ocr-text'}-${selectionId}`) as HTMLElement
    if (textField) {
      const currentStyle = textField.style.cssText
      const newStyle = currentStyle.replace(/white-space:\s*[^;]+/, `white-space: ${enabled ? 'pre-wrap' : 'pre'}`)
      textField.style.cssText = newStyle
    }
  }

  private toggleConfidenceHighlighting(selectionId: string, enabled: boolean): void {
    localStorage.setItem(`confidence_highlight_${selectionId}`, JSON.stringify(enabled))
    
    // Auto-switch to Original tab when enabling confidence highlighting
    if (enabled) {
      const tabToggle = document.querySelector(`.ocr-tab-toggle[data-selection-id="${selectionId}"]`) as HTMLInputElement
      if (tabToggle && tabToggle.checked) {
        tabToggle.checked = false
        this.toggleOCRTab(selectionId, false)
      }
    }
    
    // Re-render the card to apply highlighting
    const selections = this.appState.getSelections()
    this.renderOCRProcessingCards(selections)
  }

  private toggleNumberHighlighting(selectionId: string, enabled: boolean): void {
    localStorage.setItem(`number_highlight_${selectionId}`, JSON.stringify(enabled))
    
    // Re-render the card to apply highlighting
    const selections = this.appState.getSelections()
    this.renderOCRProcessingCards(selections)
  }

  private async processIndividualFormatting(selection: any): Promise<void> {
    try {
      // Check if LLM has already been used for this selection
      const operationKey = `clean_format_${selection.id}`
      if (!llmUsageManager.canUseLLM(operationKey)) {
        console.log('❌ LLM already used for clean formatting of selection:', selection.id)
        this.toast.warning('Already Cleaned', 'This selection has already been cleaned')
        return
      }
      
      // Add styles for the animation
      if (!document.querySelector('#clean-format-animation')) {
        const styleTag = document.createElement('style')
        styleTag.id = 'clean-format-animation'
        styleTag.innerHTML = `
          @keyframes pulseGlow {
            0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
            70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
            100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
          }
          .btn-cleaning{animation:pulseGlow 1.2s ease-in-out infinite;}
        `
        document.head.appendChild(styleTag)
      }

      const btn = document.querySelector(`.clean-format-btn[data-selection-id="${selection.id}"]`) as HTMLButtonElement

      if (btn) {
        btn.innerHTML = '🧹 Cleaning...'
        btn.classList.add('btn-cleaning')
        btn.disabled = true
      }

      // Process the formatting - using a simple text cleaning approach for now
      const cleanedText = this.cleanTextFormatting(selection.extracted_text)
      
      if (cleanedText) {
        // Update the selection with cleaned text
        const selections = this.appState.getSelections()
        const updatedSelections = selections.map(sel => 
          sel.id === selection.id ? { ...sel, edited_text: cleanedText } : sel
        )
        this.appState.setSelections(updatedSelections)
        
        // Mark LLM usage for this selection
        llmUsageManager.markLLMUsed(operationKey)
        
        // Enable text wrap by default after cleaning - always set for 'edited' tab since that's where cleaned text goes
        localStorage.setItem(`wrap_${selection.id}_edited`, 'true')
        
        // Switch to edited tab to show the cleaned text
        localStorage.setItem(`tab_${selection.id}`, 'edited')
        
        // Preserve the current text box size when cleaning - don't let it auto-resize
        const currentEditedSize = localStorage.getItem(`manual_size_edited_${selection.id}`)
        if (!currentEditedSize) {
          // If no manual size is saved, save the current calculated size to prevent auto-resize
          const currentHeight = this.calculateContentHeight(selection.edited_text || selection.extracted_text || '', selection.font_size ?? 14)
          localStorage.setItem(`manual_size_edited_${selection.id}`, JSON.stringify({
            width: '100%',
            height: currentHeight
          }))
        }
        
        // Re-render the card
        this.renderOCRProcessingCards(updatedSelections)
        
        this.toast.success('Formatting Cleaned', 'Text has been cleaned and formatted with text wrap enabled')
      } else {
        throw new Error('Formatting cleaning failed')
      }
    } catch (error) {
      console.error('Failed to clean formatting:', error)
      this.toast.error('Failed to clean formatting')
    } finally {
      const btn = document.querySelector(`.clean-format-btn[data-selection-id="${selection.id}"]`) as HTMLButtonElement
      if (btn) {
        btn.innerHTML = '🧹 Clean-Up Formatting'
        btn.classList.remove('btn-cleaning')
        btn.disabled = false
      }
    }
  }

  private cleanTextFormatting(text: string): string {
    if (!text) return ''
    
    // Basic text cleaning and formatting
    let cleaned = text
    
    // Remove extra whitespace
    cleaned = cleaned.replace(/\s+/g, ' ')
    
    // Fix common OCR errors
    cleaned = cleaned.replace(/\bl\b/g, 'I') // lowercase l to uppercase I
    cleaned = cleaned.replace(/\b0\b/g, 'O') // zero to letter O in words
    cleaned = cleaned.replace(/\b5\b/g, 'S') // 5 to S in words
    
    // Fix spacing around punctuation
    cleaned = cleaned.replace(/\s+([,.!?;:])/g, '$1')
    cleaned = cleaned.replace(/([,.!?;:])\s*([,.!?;:])/g, '$1 $2')
    
    // Fix spacing around parentheses
    cleaned = cleaned.replace(/\s*\(\s*/g, ' (')
    cleaned = cleaned.replace(/\s*\)\s*/g, ') ')
    
    // Fix spacing around quotes
    cleaned = cleaned.replace(/\s*"\s*/g, ' "')
    cleaned = cleaned.replace(/\s*"\s*/g, '" ')
    
    // Trim and return
    return cleaned.trim()
  }

  private renderConfidenceHighlightedText(text: string, wordData: any, threshold: number, highlightNumbers: boolean, highlightConfidence: boolean): string {
    return this.uiHelpers.renderConfidenceHighlightedText(text, wordData, threshold, highlightNumbers, highlightConfidence)
  }

  private syncCardHeights(): void {
    // Sync heights of cards for better visual consistency
    const cards = document.querySelectorAll('.ocr-processing-card')
    if (cards.length <= 1) return

    let maxHeight = 0
    cards.forEach(card => {
      const height = (card as HTMLElement).offsetHeight
      if (height > maxHeight) maxHeight = height
    })

    cards.forEach(card => {
      (card as HTMLElement).style.minHeight = `${maxHeight}px`
    })
  }
}